import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../hooks/useFavorites';
import Dropdown from 'react-bootstrap/Dropdown';
import BrandMark from './BrandMark';
import FavoritesDrawer from './FavoritesDrawer';

const MENU = [
  { path: '/', label: 'Inicio' },
  { path: '/catalogo', label: 'Catálogo' },
  { path: '/caracteristicas-consultorio', label: 'Consultorio' },
  { path: '/sobre-nosotros', label: 'Sobre Nosotros' },
  { path: '/contacto', label: 'Contacto' },
];

const NAV_FX_MS = 720;

export default function Navbar() {
  const { getTotalItems } = useCart();
  const { favoritesCount } = useFavorites();
  const { user, logout, isDoctor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const totalItems = getTotalItems();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [stuck, setStuck] = React.useState(false);
  const [favoritesOpen, setFavoritesOpen] = React.useState(false);
  const [favFx, setFavFx] = React.useState(null);
  const [cartFx, setCartFx] = React.useState(null);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const syncNavbarOffset = () => {
      const height = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--np-navbar-offset', `${height}px`);
    };

    syncNavbarOffset();
    const ro = new ResizeObserver(syncNavbarOffset);
    ro.observe(el);
    window.addEventListener('resize', syncNavbarOffset);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncNavbarOffset);
      document.documentElement.style.removeProperty('--np-navbar-offset');
    };
  }, []);

  React.useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    const onFavPulse = (e) => {
      const mode = e.detail?.mode === 'remove' ? 'remove' : 'add';
      setFavFx(mode);
      window.setTimeout(() => setFavFx(null), NAV_FX_MS);
    };
    const onCartPulse = (e) => {
      const mode = e.detail?.mode === 'remove' ? 'remove' : 'add';
      setCartFx(mode);
      window.setTimeout(() => setCartFx(null), NAV_FX_MS);
    };
    window.addEventListener('favorites:pulse', onFavPulse);
    window.addEventListener('cart:pulse', onCartPulse);
    return () => {
      window.removeEventListener('favorites:pulse', onFavPulse);
      window.removeEventListener('cart:pulse', onCartPulse);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`/catalogo?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <div ref={wrapRef} className={`np-navbar-wrap ${stuck ? 'is-stuck' : ''}`}>
      {/* Píldora principal del navbar */}
      <nav className="np-navbar">
        <BrandMark to="/" ariaLabel="Inicio" />

        <form onSubmit={handleSearch} className="np-search">
          <input
            type="text"
            placeholder="Buscar insumos dentales..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" className="np-search-btn" aria-label="Buscar">
            <i className="bi bi-search" />
          </button>
        </form>

        <div className="np-nav-actions">
          {isDoctor() && (
            <Link to="/consultorio" className="np-action-pill d-none d-md-inline-flex">
              <i className="bi bi-hospital" />
              Consultorio
            </Link>
          )}

          <button
            type="button"
            id="np-fav-fly-target"
            className={`np-icon-circle heart np-fav-nav-btn d-none d-sm-inline-flex${favoritesCount > 0 ? ' is-favorite' : ''}${favFx === 'add' ? ' np-fav-target--add' : ''}${favFx === 'remove' ? ' np-fav-target--remove' : ''}`}
            aria-label={favoritesCount > 0 ? `Favoritos (${favoritesCount})` : 'Favoritos'}
            aria-expanded={favoritesOpen}
            onClick={() => setFavoritesOpen(true)}
          >
            <span className="np-fav-icon-shell" aria-hidden="true">
              {favFx === 'remove' ? (
                <i className="bi bi-heartbreak-fill" />
              ) : (
                <i className={`bi ${favoritesCount > 0 ? 'bi-heart-fill' : 'bi-heart'}`} />
              )}
            </span>
            {favoritesCount > 0 && (
              <span
                className={`np-icon-badge${favFx === 'add' ? ' np-fav-badge--pop' : ''}${favFx === 'remove' ? ' np-fav-badge--shrink' : ''}`}
              >
                {favoritesCount}
              </span>
            )}
          </button>

          <Link
            to="/carrito"
            id="np-cart-fly-target"
            className={`np-icon-circle np-cart-nav-btn${cartFx === 'add' ? ' np-cart-target--add np-cart-target--bump' : ''}${cartFx === 'remove' ? ' np-cart-target--remove np-cart-target--bump' : ''}`}
            aria-label="Carrito"
          >
            <span className="np-cart-icon-shell" aria-hidden="true">
              <i className="bi bi-bag" />
            </span>
            {totalItems > 0 && (
              <span
                className={`np-icon-badge${cartFx === 'add' ? ' np-cart-badge--pop' : ''}${cartFx === 'remove' ? ' np-cart-badge--shrink' : ''}`}
              >
                {totalItems}
              </span>
            )}
          </Link>

          {user ? (
            <Dropdown align="end">
              <Dropdown.Toggle as="button" className="np-action-pill" style={{ background: '#fff' }}>
                <span
                  className="rounded-circle text-white d-inline-flex align-items-center justify-content-center"
                  style={{ width: 24, height: 24, fontSize: '0.7rem', background: '#2262e2', fontWeight: 700 }}
                >
                  {user.nombre?.charAt(0).toUpperCase() || 'U'}
                </span>
                <span className="d-none d-md-inline">{user.nombre?.split(' ')[0]}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="shadow border-0 mt-2" style={{ borderRadius: 18, padding: 8 }}>
                <div className="px-3 py-2 border-bottom small">
                  <div className="fw-bold text-dark">{user.nombre}</div>
                  <div className="text-muted text-truncate" style={{ maxWidth: 200 }}>{user.email}</div>
                </div>
                {isDoctor() && (
                  <Dropdown.Item as={Link} to="/consultorio">
                    <i className="bi bi-hospital me-2" />Mi Consultorio
                  </Dropdown.Item>
                )}
                <Dropdown.Item as={Link} to="/mi-cuenta">
                  <i className="bi bi-person me-2" />Mi Cuenta
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={logout} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2" />Cerrar sesión
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <Link to="/login" className="np-action-pill">
              <i className="bi bi-person" />
              <span className="d-none d-md-inline">Ingresar</span>
            </Link>
          )}
        </div>
      </nav>

      {/* Navegación principal de la tienda */}
      <div className="np-categories">
        <button className="np-cat-filter" aria-label="Filtros" type="button">
          <i className="bi bi-sliders" />
        </button>
        {MENU.map((m) => (
          <Link
            key={m.path}
            to={m.path}
            className={`np-cat-pill ${isActive(m.path) ? 'active' : ''}`}
          >
            {m.label}
          </Link>
        ))}
      </div>

      <FavoritesDrawer open={favoritesOpen} onClose={() => setFavoritesOpen(false)} />
    </div>
  );
}
